import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {deleteMyAccount, verifyIPAddressNZ, getUserProfile, signIn, updateUserProfile} from "./HttpService";

import {useError} from "./ErrorContext";
import {UserProfileData} from "./Types";

export const throwError = (message: string): never => {
  throw new Error(message);
};

type AuthContextType = {
  isLoggedIn: boolean;
  isIPBlocked: boolean | undefined;
  userProfile: UserProfileData | undefined;
  updateProfile: (profileData: UserProfileData) => void;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  deleteAccount: () => void;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider = ({ children }: {children: ReactNode}) => {
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [isIPBlocked, setIsIPBlocked] = useState<boolean | undefined>(undefined);
  const [userProfile, setUserProfile] = useState<UserProfileData | undefined>(undefined);
  const { setError } = useError();

  // check IP address is allowed to access the app
  const checkIPAddress = useCallback(async () => {
    try {
      // Verify IP using backend API
      const nzIP = await verifyIPAddressNZ();
      setIsIPBlocked(!nzIP);
    } catch (error) {
      console.error('Error verifying IP address:', error);
      setError('An unexpected error occurred while verifying IP address.');
    }
  }, [setError]);

  // fetch profile
  const getProfile = useCallback (async () => {
    try {
      const profileData = await getUserProfile();
      setUserProfile(profileData);
    } catch (error: any) {
      setError(error.message);
      console.error(error);
    }
  }, [setError]);

  useEffect(() => {
    // Check the local storage for login status on initial load
    const token = localStorage.getItem('refresh_token');

    if (token && !isLoggedIn) {
      getProfile().then(() => setIsLoggedIn(true));
    }

    checkIPAddress();
  }, [checkIPAddress, isLoggedIn, getProfile, setError]);

  const updateProfile = async (profileData: UserProfileData) => {
    await updateUserProfile(profileData);
    setUserProfile({ ...profileData, password: '' });
  }

  const login = async (username: string, password: string) => {
    try {
      const { accessToken, expiresIn, refreshToken, habits }  = await signIn({username, password});
      localStorage.setItem('access_token', accessToken);
      localStorage.setItem('expires_in', expiresIn);
      localStorage.setItem('refresh_token', refreshToken);
      localStorage.setItem('habits', JSON.stringify(habits));
      await getProfile();
      setIsLoggedIn(true);
      console.log('Sign-in successful');
    } catch (err: any) {
      await logout();
      console.error('Error during sign-in:', err);
      throw err;
    }
  };

  const logout = async () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setIsLoggedIn(false);
  };

  const deleteAccount = async () => {
    try {
      await deleteMyAccount();
      console.log('delete account successful');
      await logout();
    } catch (err) {
      console.error('Error during delete account:', err);
      throw err;
    }
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, isIPBlocked, userProfile, updateProfile, login, logout, deleteAccount }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
